import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type AdminRole = "super_admin" | "support_agent" | "billing_admin" | "read_only";

const PERMISSION_MAP: Record<string, AdminRole[]> = {
  impersonate: ["super_admin", "support_agent"],
  change_flags: ["super_admin"],
  change_billing: ["super_admin", "billing_admin"],
  view_audit: ["super_admin", "support_agent", "billing_admin", "read_only"],
  send_announcements: ["super_admin"],
  invite_admins: ["super_admin"],
  change_admin_roles: ["super_admin"],
  suspend_tenants: ["super_admin", "billing_admin"],
  view_tenants: ["super_admin", "support_agent", "billing_admin", "read_only"],
  deactivate_users: ["super_admin", "support_agent"],
};

export function useAdminRole() {
  const { user } = useAuth();

  const { data: adminUser, isLoading } = useQuery({
    queryKey: ["admin-role", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("admin_users")
        .select("*")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .maybeSingle();
      if (error) {
        console.error("useAdminRole query failed:", error.message);
        return null;
      }
      return data as { role: AdminRole; email: string; user_id: string } | null;
    },
    enabled: !!user,
    staleTime: 5 * 60_000,
  });

  const role = (adminUser?.role ?? null) as AdminRole | null;

  const can = (action: string): boolean => {
    if (!role) return false;
    const allowed = PERMISSION_MAP[action];
    if (!allowed) return false;
    return allowed.includes(role);
  };

  return { role, can, isLoading, isAdmin: !!adminUser };
}
